import React from 'react';
import breakfastBg from '../../../assets/chat/meal/breakfast.png';
import lunchBg from '../../../assets/chat/meal/lunch.png';
import dinnerBg from '../../../assets/chat/meal/dinner.png';
import NearbyPlaceRow from './NearbyPlaceRow';
import type { MealQueryContent, NearbyPlace } from '../../../data-access/api/science/openapi/api';
import './richContext.scss';
import clsx from 'clsx';

type Props = {
  p: Record<string, unknown>;
  enrichmentPending: boolean;
  fromAssistantRichContextCard?: boolean;
};

const mealTypeEmoji = (mealType?: string): string => {
  const normalized = typeof mealType === 'string' ? mealType.toLowerCase() : '';
  if (normalized === 'breakfast') return '🌅';
  if (normalized === 'lunch') return '🥗';
  if (normalized === 'dinner') return '🌙';
  return '🍽️';
};

const mealTypeLabel = (mealType?: string): string => {
  if (!mealType) return 'Meal';
  return mealType.charAt(0).toUpperCase() + mealType.slice(1);
};

const mealTypeBgMap = {
  breakfast: breakfastBg,
  lunch: lunchBg,
  dinner: dinnerBg,
};

const formatNutrientLabel = (key: string): string => {
  const parts = key.split('_');
  const unit = parts[parts.length - 1];
  const hasUnit = ['g', 'mg', 'mcg', 'kcal'].includes(unit);
  const nameParts = hasUnit ? parts.slice(0, -1) : parts;
  const name = nameParts.join(' ');

  return hasUnit ? `${name} (${unit})` : name;
};

const MealDetailsContent: React.FC<Props> = ({
  p,
  enrichmentPending,
  fromAssistantRichContextCard = false,
}) => {
  const tags = Array.isArray(p.dish_tags) ? (p.dish_tags as string[]) : [];
  const mealDetails = Array.isArray(p.meal_details)
    ? (p.meal_details as NonNullable<MealQueryContent['meal_details']>)
    : [];
  const topLevelFoodItems = Array.isArray(p.food_items)
    ? (p.food_items as Array<{ name?: unknown; amount?: unknown }>)
    : Array.isArray(p.foodItems)
      ? (p.foodItems as Array<{ name?: unknown; amount?: unknown }>)
      : [];
  const topLevelType = typeof p.type === 'string' ? p.type : 'meal';
  const topLevelStartTime =
    typeof p.startTime === 'string'
      ? p.startTime
      : typeof p.start_time === 'string'
        ? p.start_time
        : undefined;
  const topLevelEndTime =
    typeof p.endTime === 'string'
      ? p.endTime
      : typeof p.end_time === 'string'
        ? p.end_time
        : undefined;
  const topLevelTitle = typeof p.title === 'string' ? p.title : '';

  const effectiveMealDetails =
    mealDetails.length > 0
      ? mealDetails
      : topLevelFoodItems.length > 0 || topLevelStartTime || topLevelEndTime || topLevelTitle
        ? [
            {
              type: topLevelType,
              startTime: topLevelStartTime,
              endTime: topLevelEndTime,
              title: topLevelTitle,
              food_items: topLevelFoodItems,
            },
          ]
        : [];

  const nearbyPlaces = Array.isArray(p.nearby_places) ? (p.nearby_places as NearbyPlace[]) : [];
  const hasPins = nearbyPlaces.length > 0;
  const macroPills =
    p.macros && typeof p.macros === 'object'
      ? Object.entries(p.macros as Record<string, number>).filter(([, value]) => value > 0)
      : [];
  const microPills =
    p.micros && typeof p.micros === 'object'
      ? Object.entries(p.micros as Record<string, unknown>).filter(
          ([, value]) => typeof value === 'number' && value > 0
        )
      : [];

  const renderedMealRows = effectiveMealDetails
    .map((meal, i) => {
      const mealType = typeof (meal as any).type === 'string' ? (meal as any).type : 'meal';
      const title =
        typeof (meal as any).title === 'string'
          ? String((meal as any).title).trim()
          : typeof (meal as any).food?.name === 'string'
            ? String((meal as any).food.name)
            : '';
      const foodItemsRaw = Array.isArray((meal as any).foodItems)
        ? ((meal as any).foodItems as Array<{ name?: unknown; amount?: unknown }>)
        : Array.isArray((meal as any).food_items)
          ? ((meal as any).food_items as Array<{ name?: unknown; amount?: unknown }>)
          : [];
      const foodItems = foodItemsRaw
        .map((foodItem) => {
          const name = typeof foodItem?.name === 'string' ? foodItem.name.trim() : '';
          if (!name) return '';
          if (typeof foodItem?.amount === 'number' || typeof foodItem?.amount === 'string') {
            return `${name} (${foodItem.amount})`;
          }
          return name;
        })
        .filter(Boolean);

      if (!title && foodItems.length === 0 && !mealType) return null;
      return { key: `${mealType}_${title}_${i}`, mealType, title, foodItems };
    })
    .filter(Boolean) as Array<{
    key: string;
    mealType: string;
    title: string;
    foodItems: string[];
  }>;

  const hasHeaderMetadata =
    !!p.venue_name ||
    tags.length > 0 ||
    macroPills.length > 0 ||
    microPills.length > 0 ||
    typeof p.rating === 'number';
  const hasRenderableDetails = renderedMealRows.length > 0;

  if (!hasHeaderMetadata && !hasRenderableDetails && !hasPins && !enrichmentPending) return null;

  return (
    <div className="rc-content-body">
      {!!p.venue_name && <span className="rc-content-meta">{String(p.venue_name)}</span>}

      <div className="rc-pill-row">
        {tags.map((tag, i) => (
          <div key={`${tag}_${i}`} className="rc-tag-pill">
            <span className="rc-tag-pill-text">{tag}</span>
          </div>
        ))}
        {macroPills.map(([macro, value]) => (
          <div key={`macro_${macro}`} className="rc-tag-pill">
            <span className="rc-tag-pill-text">
              {formatNutrientLabel(macro)}: {value.toFixed(1)}
            </span>
          </div>
        ))}
        {microPills.map(([micro, value]) => (
          <div key={`micro_${micro}`} className="rc-tag-pill">
            <span className="rc-tag-pill-text">
              {formatNutrientLabel(micro)}: {typeof value === 'number' ? value : String(value)}
            </span>
          </div>
        ))}
        {typeof p.rating === 'number' && (
          <div className="rc-tag-pill">
            <span className="rc-tag-pill-text">★ {(p.rating as number).toFixed(1)}</span>
          </div>
        )}
      </div>

      {renderedMealRows.map((meal) => (
        <div
          key={meal.key}
          className={clsx('rc-meal-row-card', {
            'rc-meal-row-card--assistant-chat': fromAssistantRichContextCard,
          })}
        >
          <div
            className={clsx('rc-meal-row-image-container', {
              'rc-meal-row-image-container--assistant-chat': fromAssistantRichContextCard,
            })}
          >
            <img
              src={mealTypeBgMap[meal.mealType]}
              className="rc-meal-row-image"
              alt={meal.mealType}
            />
          </div>
          <div className="rc-meal-row-text-wrap">
            <div className="rc-meal-row-type-label-container">
              <span className="rc-meal-row-type-label">
                {mealTypeEmoji(meal.mealType)} {mealTypeLabel(meal.mealType)}
              </span>
            </div>
            <span className="rc-meal-row-main-text">
              {meal.title || mealTypeLabel(meal.mealType)}
            </span>
            {meal.foodItems.length > 0 && (
              <div className="rc-meal-ingredient-list">
                {meal.foodItems.map((item, i) => (
                  <span key={`${meal.key}_${i}`} className="rc-meal-ingredient-text">
                    • {item}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      {hasPins ? (
        <div className="rc-nearby-list">
          {nearbyPlaces.map((place, i) => (
            <NearbyPlaceRow key={i} place={place} />
          ))}
        </div>
      ) : enrichmentPending ? (
        <div className="rc-skeleton-group">
          <div className="rc-skeleton-row" />
          <div className="rc-skeleton-row rc-skeleton-row--short" />
        </div>
      ) : null}
    </div>
  );
};

export default MealDetailsContent;
